import { FC, useEffect, useState, useMemo, useCallback } from 'react';
import { useToggle } from 'utils/hooks';
import {} from 'react-table';
import table from 'containers/ChannelManagement/Channel/ChannelProducts/ServiceFeeContainer/ServiceFeeTable/ServiceFeeTable.module.css';
import btnStyle from 'components/PartnerDetails/PartnerDetails.module.css';
import { ReactComponent as EditIcon } from 'assets/icons/ic-save.svg';
import { ServiceFeeTableProps } from 'containers/ChannelManagement/Channel/ChannelProducts/types';
import ChannelTable from 'containers/ChannelManagement/Channel/ChannelTable/ChannelTable';
import { Button } from '@salesforce/design-system-react/module/components';
import cx from 'classnames';
import ChannelProductModal from 'containers/ChannelManagement/Channel/ChannelProducts/ChannelProductsModal/ChannelProductModal';
import { saveUpdateShare } from 'containers/ChannelManagement/Channel/ChannelProducts/ChannelProductQuery';
import { useSelector } from 'react-redux';
import { ReducerStateType } from 'redux/modules/reducers';
import FullPageLoader from 'components/Loader/FullPageLoader/FullPageLoader';
import SuccessModal, {
	SuccessModalActions,
	SuccessModalBody,
	SuccessText,
} from 'components/Modal/SuccessChannelModal';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import AccessDeniedModal from 'containers/ChannelManagement/Permission/AccessDeniedModal';
import ErrorModal from 'containers/ChannelManagement/Drafts/ErrorModal/ErrorModal';

const ServiceFeeTable: FC<ServiceFeeTableProps> = ({
	itemKey,
	columns,
	data,
	title,
	serviceFeeType,
	nonShareableColumn,
	control,
	sfConfig,
	permission,
	selectedChannel,
	product,
	customStyles,
	hasEditServiceFeePermission,
	channelTypes,
}) => {
	const _userInfo = useSelector<ReducerStateType, string>(
		(state) => state.userInfo
	);
	const [enableEdit, setEnableEdit] = useState<boolean>(
		customStyles === undefined
	); // temporary use of customStyles: init value=true
	const [showConfirmationModal, setShowConfirmationModal] =
		useState<boolean>(false);
	const [requestPayload, setRequestPayload] = useState({});
	const [save, setSave] = useState(false);
	const [existingShare, setExistingShare] = useState([]);
	const [isReset, setIsReset] = useState(true);
	const [errorHeader, setErrorHeader] = useState<ReactNode>('');
	const [loadingMessage, setLoadingMessage] = useState('');
	const [successMessage, setSuccessMessage] = useState(<></>);
	const [errorMessage, setErrorMessage] = useState(<></>);
	const [share, setShare] = useState([
		{
			productId: 0,
			id: 0,
			partnerShare: 0,
			bayadShare: 0,
			hasError: false,
			message: '',
		},
	]);

	const styles = customStyles
		? customStyles
		: {
				...table,
				...btnStyle,
		  };

	const {
		value: isLoading,
		valueOn: showLoading,
		valueOff: hideLoading,
	} = useToggle();

	const {
		value: isErrorRetryShowing,
		valueOn: showErrorRetry,
		valueOff: hideErrorRetry,
	} = useToggle();

	const {
		value: isSuccessModalOpen,
		valueOn: showSuccessModal,
		valueOff: hideSuccessModal,
	} = useToggle();

	const {
		value: hasPermission,
		valueOn: showPermissionErrorModal,
		valueOff: hidePermissionErrorModal,
	} = useToggle();

	useEffect(() => {
		if (sfConfig && sfConfig.length > 0) {
			setExistingShare(
				sfConfig.map((item) => {
					return {
						...item,
						shares: item.shares.map((val) => {
							return {
								productId: val.productId,
								id: val.tierNumber || val.channelTypeId,
								partnerShare: val.partnerShare,
								bayadShare: val.bayadShare,
								hasError: false,
								message: '',
							};
						}),
					};
				})
			);
		}
	}, [sfConfig]);

	useEffect(() => {
		if (isReset && existingShare && existingShare.length > 0) {
			const extractedShares = existingShare.flatMap((item) => item.shares);
			setShare(extractedShares);
			setIsReset(false);
		}
	}, [existingShare, isReset]);

	const setPartnerShareAmount = useCallback(
		(event: any, name, rowData) => {
			const newShareValue = event.target.value;
			const isPercentage = nonShareableColumn.isPercentage;
			const isAlternativePercentage =
				sfConfig.find((v) => v.config.productId === product.productId).config
					.schemeType === 'PERCENTAGE';
			if (
				(isPercentage || isAlternativePercentage) &&
				(newShareValue < 0 || newShareValue > 100)
			) {
				setShare((prevShare) =>
					prevShare.map((item) => {
						if (
							item.productId === product.productId &&
							item.id === (rowData.tierNumber || rowData.channelTypeId)
						) {
							return {
								...item,
								partnerShare: 0,
								bayadShare: 0,
								hasError: true,
								message: 'Input value 0-100',
							};
						}
						return item;
					})
				);
				return;
			}

			if (newShareValue === '.00' || newShareValue === '') {
				setShare((prev) =>
					prev.filter(
						(item) =>
							!(
								item.productId === product.productId &&
								item.id === (rowData.tierNumber || rowData.channelTypeId)
							)
					)
				);
			}
			setShare((prevShare) => {
				const updatedShare = prevShare.map((item) => {
					if (
						item.productId === product.productId &&
						item.id === (rowData.tierNumber || rowData.channelTypeId)
					) {
						const updatedPartnerShare = parseFloat(newShareValue);
						const partnerShare =
							!isNaN(updatedPartnerShare) && isFinite(updatedPartnerShare)
								? updatedPartnerShare
								: 0;
						return {
							...item,
							partnerShare,
							bayadShare:
								partnerShare !== 0
									? Math.abs(
											parseFloat(partnerShare.toString()) -
												parseFloat(
													isPercentage || isAlternativePercentage
														? rowData.totalAmountForSfShareRate
														: rowData.totalAmountForSfShare
												)
									  ).toFixed(2)
									: 0,
							hasError: false,
							message: '',
						};
					}
					return item;
				});

				const isNewRow = !prevShare.some(
					(item) =>
						item.productId === product.productId &&
						item.id === (rowData.tierNumber || rowData.channelTypeId)
				);

				if (isNewRow && newShareValue !== '.00' && newShareValue !== '') {
					updatedShare.push({
						productId: product.productId,
						id: rowData.tierNumber || rowData.channelTypeId,
						partnerShare: newShareValue,
						bayadShare: Math.abs(
							parseFloat(newShareValue) -
								parseFloat(
									isPercentage || isAlternativePercentage
										? rowData.totalAmountForSfShareRate
										: rowData.totalAmountForSfShare
								)
						).toFixed(2),
						hasError: false,
						message: '',
					});
				}
				return updatedShare;
			});
			// event.target.focus();
		},
		[nonShareableColumn, share, product.productId]
	);

	const [regularOrPercentageState, setRegularOrPercentage] = useState('');
	const tableProps = useMemo(() => {
		const regularOrPercentage = sfConfig.find(
			(v) => v.config.productId === product.productId
		).config.schemeType;
		setRegularOrPercentage(regularOrPercentage);
		return {
			data: data,
			columnsParam: columns,
			key: itemKey,
			tableClassName:
				regularOrPercentage === 'REGULAR' ||
				regularOrPercentage === 'PERCENTAGE'
					? styles.tableRegular
					: styles.table,
			bodyClassName:
				regularOrPercentage === 'REGULAR' ||
				regularOrPercentage === 'PERCENTAGE'
					? styles.tableBodyNoborder
					: styles.tableBody,
			headerClassName:
				regularOrPercentage === 'REGULAR' ||
				regularOrPercentage === 'PERCENTAGE'
					? styles.tableHeaderNoborder
					: styles.tableHeader,
			containerClassName:
				regularOrPercentage === 'REGULAR' ||
				regularOrPercentage === 'PERCENTAGE'
					? styles.tableContainerRegular
					: styles.tableContainerStyle,
			onChange: setPartnerShareAmount,
			serviceFeeType: regularOrPercentage, // pass schemeType
			toEdit: enableEdit,
			shares: share.filter((v) => v.productId === product.productId),
			nonShareableColumn: nonShareableColumn,
			control: control,
			channelTypes: channelTypes,
		};
	}, [
		data,
		columns,
		itemKey,
		styles.table,
		styles.tableBody,
		styles.tableHeader,
		setPartnerShareAmount,
		serviceFeeType,
		enableEdit,
		share,
		nonShareableColumn,
		control,
		product.productId,
		sfConfig,
		styles.tableBodyNoborder,
		styles.tableHeaderNoborder,
	]);

	const handleCancelBtnClick = () => {
		setEnableEdit(!enableEdit);
		setIsReset(true);
	};

	const [shareData, setShareData] = useState({
		valid: false,
		data: [],
	});

	useEffect(() => {
		setShareData({
			valid: !share.find(
				(v) => v.hasError === true && v.productId === product.productId
			)?.hasError
				? share.filter((v) => v.productId === product.productId).length ===
				  data.length
				: false,
			data: data,
		});
	}, [share, data, product.productId]);

	const saveServiceFeeShare = () => {
		if (hasEditServiceFeePermission) {
			return showPermissionErrorModal();
		}
		setShowConfirmationModal(true);
	};

	const handleCancelSubmit = () => {
		setShowConfirmationModal(false);
		setIsReset(true);
	};

	const handleClickRetry = () => {
		// setSave(true);
	};

	const handleConfirm = useCallback(() => {
		setShowConfirmationModal(false);
		setLoadingMessage('Please wait while we save shares.');
		showLoading();
		const config = sfConfig.find(
			(v) => v.config.productId === product.productId
		);
		let submittedBy = '';
		let submittedByEmail = '';
		if (config.shares.length === 0) {
			submittedBy = _userInfo.username;
			submittedByEmail = _userInfo.emailAddress;
		}
		const payload = config.values.map((item) => {
			const shareItem = share.find(
				(v) =>
					v.productId === product.productId &&
					v.id === (item.tierNumber || item.channelTypeId)
			) || { partnerShare: 0, bayadShare: 0 };
			const pshare = String(shareItem.partnerShare || 0);
			const bshare = String(shareItem.bayadShare || 0);
			return {
				channel_id: selectedChannel.id,
				product_id: product.productId,
				tier_number: item.tierNumber,
				channel_type_id: item.channelTypeId,
				share_type: config.config.nonShareableRateType,
				partner_share: parseFloat(pshare),
				bayad_share: parseFloat(bshare),
				total_share: parseFloat(
					Math.abs(parseFloat(pshare) + parseFloat(bshare)).toFixed(2)
				),
				remarks: 'test', // not included on saving but existing column
				updated_by: _userInfo.username,
				updated_by_email: _userInfo.emailAddress,
				submitted_by: submittedBy,
				submitted_by_email: submittedByEmail,
			};
		});
		setRequestPayload({
			share: payload,
		});
		setSave(true);
	}, [
		sfConfig,
		share,
		product.productId,
		_userInfo.username,
		_userInfo.emailAddress,
		selectedChannel.id,
		setRequestPayload,
		setSave,
		setLoadingMessage,
		showLoading,
	]);

	useEffect(() => {
		const submitShare = async () => {
			try {
				const res = await saveUpdateShare(
					requestPayload,
					selectedChannel.id,
					product.productId
				);
				hideLoading();
				setSuccessMessage(
					<>
						Your created service fee share was submitted
						<br />
						for approval.
					</>
				);
				showSuccessModal();
			} catch (err) {
				const existingRequest = new RegExp("There are existing service fee share request that are pending for approval for product".split(" ").join("\\s*\\w*\\s*"), "i")
				setErrorHeader('Error!');
				const additionalMessage =
					err?.response?.data?.message &&
					existingRequest.test(err?.response?.data?.message);
				hideLoading();
				if(additionalMessage) {
					setErrorHeader('Service Fee Share not submitted');
					setErrorMessage(
						<>
							There is a pending service fee share request for approval. Please check the request and try again.
						</>
					)
				} else { 
					setErrorMessage(
						<>
							Partner Share must not be more than the <br />
							Total Amount/Percentage for SF Share.
						</>
					);
				}
				showErrorRetry();
			} finally {
				setSave(false);
			}
		};

		if (save && requestPayload.share.length > 0) {
			submitShare();
		}
	}, [save, requestPayload, selectedChannel.id, product.productId]);

	return (
		<>
			<div>
				<div className={styles.tableTitle}>
					{title}
					<div style={{ marginBottom: '2vh' }}></div>
					<div className={styles.rightPanel}>
						<div className={styles.edit}>
							{/* Temporarily removed: change request deferred */}
							{/* <Button
								className={cx(
									regularOrPercentageState === 'REGULAR' ||
										regularOrPercentageState === 'PERCENTAGE'
										? styles.inLineRegular
										: styles.inLine,
									styles.linkBtn,
									styles.edit
								)}
								onClick={(v) => {
									setEnableEdit(!enableEdit);
								}}
							>
								<EditIcon />
								{enableEdit ? 'Edit' : 'Exit Edit Mode'}
							</Button> */}
						</div>
					</div>
				</div>
				<ChannelTable
					{...tableProps}
					itemKey={`table-item-${itemKey}`}
					permission
				/>

				<hr className={styles.lineDeviderEnd} />
				{/* Temporarily added */}
				{!customStyles && (
					<div className={styles.btnStyleClass}>
						<PrimaryButton
							onClick={saveServiceFeeShare}
							className={styles.confirmationModalBtn}
							disabled={permission && !shareData.valid}
						>
							Submit
						</PrimaryButton>
					</div>
				)}

				{/* Temporarily not in use: change request deferred */}
				{/* {!enableEdit && (
					<ChannelProductModal
						heading={'Service Fee Share Request'}
						removeHeading={
							regularOrPercentageState !== 'REGULAR' &&
							regularOrPercentageState !== 'PERCENTAGE'
						}
						isOpen={!enableEdit}
						message={''}
						key={`service-fee-${itemKey}-${selectedChannel.id}`}
						cancelBtnLabel="Cancel"
						confirmBtnLabel="Submit"
						onCancelBtnClick={handleCancelBtnClick}
						onClose={handleCancelBtnClick}
						onConfirmBtnClick={() => {
							setEnableEdit(!enableEdit);
							saveServiceFeeShare();
						}}
						showTablefield
						isPrimaryBtnDisabled={permission && !shareData.valid}
						tableContent={
							<div
								style={{
									overflowX: 'auto',
									margin: '0 auto',
									marginBottom: '1vh',
								}}
							>
								{regularOrPercentageState !== 'REGULAR' &&
									regularOrPercentageState !== 'PERCENTAGE' && (
										<div className={styles.contentHeader}>
											Service Fee Share Request
										</div>
									)}
								<div
									style={
										regularOrPercentageState !== 'REGULAR' &&
										regularOrPercentageState !== 'PERCENTAGE'
											? { margin: '1.9vh' }
											: { margin: '0' }
									}
								>
									<ChannelTable
										{...tableProps}
										tableClassName={
											regularOrPercentageState === 'REGULAR' ||
											regularOrPercentageState === 'PERCENTAGE'
												? styles.tableSmall
												: styles.tableBordered
										}
										itemKey={`table-item-${itemKey}`}
										permission={!permission}
									/>
								</div>
							</div>
						}
						headerClassName={styles.headerContainer}
						contentClassName={styles.contentContainer}
						containerClassName={
							regularOrPercentageState === 'REGULAR' ||
							regularOrPercentageState === 'PERCENTAGE'
								? styles.tableSmall
								: styles.modalContainer
						}
					/>
				)} */}
			</div>

			{showConfirmationModal && (
				<ChannelProductModal
					isOpen={showConfirmationModal}
					onClose={handleCancelSubmit}
					isDisabled={isLoading}
					heading={<h2 className={styles.customHeading}>Submit Request</h2>}
					message={
						<h2 className={styles.bodyHeader}>
							Are you sure you want to submit the request?
						</h2>
					}
					cancelBtnLabel="Cancel"
					confirmBtnLabel="Confirm"
					onCancelBtnClick={handleCancelSubmit}
					onConfirmBtnClick={handleConfirm}
					headerClassName={styles.headerContainer}
					containerClassName={styles.modalConfirmation}
					contentClassName={styles.modal}
				/>
			)}

			{isLoading && (
				<FullPageLoader open={isLoading} message={loadingMessage} />
			)}
			<SuccessModal open={isSuccessModalOpen} onClose={hideSuccessModal}>
				<SuccessModalBody>
					<SuccessText>Success!</SuccessText>
					<div className={styles.successMessage}>
						<p className={styles.subTextNext}>{successMessage}</p>
					</div>
				</SuccessModalBody>
				<SuccessModalActions>
					<PrimaryButton
						className={styles.doneButton}
						onClick={() => {
							hideSuccessModal();
						}}
					>
						Okay
					</PrimaryButton>
				</SuccessModalActions>
			</SuccessModal>
			<ErrorModal
				open={isErrorRetryShowing}
				onClose={hideErrorRetry}
				errorMessage={errorMessage}
				errorHeader={errorHeader}
				errorButton="Okay"
			/>
			{hasPermission && (
				<AccessDeniedModal
					open={hasPermission}
					onClose={hidePermissionErrorModal}
				/>
			)}
		</>
	);
};

export default ServiceFeeTable;
